import { NgClass } from "@angular/common";
import { HttpClient } from "@angular/common/http";
import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Router, RouterLink } from "@angular/router";
import { TrackSearchGlobalParams } from "@app/models/tracking/track-event-param-type";
import { NavigateFromSearchbarIntoCatalog, NavigateSearchbarParams } from "@app/models/tracking/track-history-param-type";
import { SearchbarPublicBase } from "@app/super/searchbar-public-base";
import { CatalogLogoImgComponent } from "@commonComponents/catalog-logo-img/catalog-logo-img.component";
import { isNullish } from "@commonHelpers/math-utils";
import { TranslatePipe } from "@commonNodeModules/@ngx-translate/core";
import { SearchHighLightPipe } from "@commonPipes/searchHightlight";
import { ViewType } from "@enums/view-type";
import { environment } from "@environments/environment";
import { Client as SearchAPI, SearchFilterId } from "@interfaces/HttpClient/SearchApiModels";
import { AnalyticsTrackingFacadeService } from "@services/analytics-tracking-facade.service";
import { BreadcrumbService } from "@services/breadcrumb.service";
import { IntervalService } from "@services/interval.service";
import { NoResultService } from "@services/no-result.service";
import { SearchResultService } from "@services/search-result.service";
import { SearchService } from "@services/search.service";
import { SearchbarService } from "@services/searchbar.service";
import { TranslationService } from "@services/translation.service";
import { ViewTypeService } from "@services/view-type.service";
import { timer } from "rxjs";

@Component({
	selector: "app-searchbar",
	templateUrl: "./searchbar.component.html",
	styleUrls: ["./searchbar.component.scss"],
	providers: [
		SearchResultService,
		SearchbarService,
		SearchService, {
			provide: SearchAPI,
			useFactory: (http: HttpClient) => new SearchAPI(http, environment.searchApi),
			deps: [HttpClient]
		}
	],
	imports: [NgClass, FormsModule, RouterLink, TranslatePipe, SearchHighLightPipe, CatalogLogoImgComponent]
})
export class SearchbarComponent extends SearchbarPublicBase implements OnInit, OnDestroy {

	@Input() useOptionalCategoryGroupFilter = false;
	@Input() searchbarExistsInHeader = false;
	@Input() applySearchTerm = false;
	@Input() isSearchResultComponent = false;
	@Input() initInputAfter: number;
	@Input() hasborder = false;
	@Output() searchCommit = new EventEmitter();
	@Input() disableFocus = false;
	private readonly GLOBAL_CATALOGCOUNT = 5;
	// Benötigt bei initialer Darstellung in Mobil bei Querformatt
	private readonly MIN_HEIGHT_FOR_INITIAL_INPUT = 500;



	constructor(
		protected router: Router,
		protected searchbarService: SearchbarService,
		private searchService: SearchService,
		protected translationService: TranslationService,
		public searchResultService: SearchResultService,
		protected breadcrumbService: BreadcrumbService,
		protected elRef: ElementRef,
		protected viewTypeService: ViewTypeService,
		protected trackingFacadeService: AnalyticsTrackingFacadeService,
		private noResultService: NoResultService,
		private intervalService: IntervalService
	) { super(translationService, breadcrumbService, router, searchbarService, elRef, viewTypeService) }

	ngOnInit() {
		if (this.applySearchTerm) {
			this.currentSearchWord = this.setSearchTerm(this.searchbarService.searchTerm);
		}
		this.init();
		this.viewTypeSubscription = this.viewTypeService.currentViewType$.subscribe(currentViewType => {
			if (!this.disableFocus && currentViewType === ViewType.Desktop && window.innerHeight > this.MIN_HEIGHT_FOR_INITIAL_INPUT && isNullish(this.initInputAfter)) {
				this.focusToInput();
			}
		})

		this.intervalService.getInterval().subscribe(() => this.sendNewTrackedElements());
		if (!this.disableFocus) {
			if (!isNullish(this.initInputAfter)) {
				timer(this.initInputAfter).subscribe(() => {
					this.focusToInput();
				})
			}
		}
	}

	async ngOnDestroy(): Promise<void> {
		this.destroy();
		this.sendNewTrackedElements();
	}

	public catalogSuggestionClicked(): void {
		this.breadcrumbService.setCatalogNavigation(undefined);
	}

	protected async navigateToSearch(term) {
		if (!term || term.length === 0) {
			this.searchbarService.searchTerm = "";
			this.emptySearch.emit();
			return;
		}

		this.searchCommit.emit();
		// Optionaler Filter: Das ? hinter der Categorie bedeutet, dass wenn diese Suche 0 Treffer ergibt, der Filter von der Suche ignoriert wird und die Suche ohne diesen Filter ausgeführt wird. Das ist genauso vom PO gewünscht und soll so sein!
		const searchFilter = this.useOptionalCategoryGroupFilter &&
			this.searchbarService.optionalCategoryGroupFilter ? SearchFilterId.Category + '?:G' +
		this.searchbarService
			.optionalCategoryGroupFilter : undefined;
		this.trackBeforeNavigate();
		this.searchbarService.setSearching(true);
		const searchResult = await this.searchService.search(
			{
				searchTerm: term,
				searchFilter,
				pageIndex: 0,
				pageSize: 30
			}
		);
		this.searchbarService.setSearching(false);
		// spezialfall abbruch suche
		if (isNullish(searchResult)) {
			return;
		}
		if (searchResult?.totalCatalogItemHitsCount === 0 && searchResult?.catalogHits?.length === 0) {

			this.trackingFacadeService.handleTrackEvent(new TrackSearchGlobalParams(
				term,
				searchFilter,
				0,
				30,
				searchResult.catalogHits.length,
				searchResult.totalCatalogItemHitsCount,
			))

			this.noResultService.open({ searchTerm: term, centered: true, searchingType: searchResult.searchingType })
		} else {
			await this.searchnavigate(term, searchFilter);
		}
	}

	public get placeholder() {
		return this.searchbarService.searchTerm ? this.translationService.getByKey('searchbar.current-searchterm', { value: this.searchbarService.searchTerm }) : this.translationService.getByKey('searchbar.placeholder')
	}

	public async changeSearchSuggestions(value: string) {
		this.focusToInput();
		this.suggestions = await this.searchService.getSuggestions(
			value,
			this.useOptionalCategoryGroupFilter && this.searchbarService.optionalCategoryGroupFilter !== 0
				? this.searchbarService.optionalCategoryGroupFilter
				: undefined
		);
		if (!isNullish(this.suggestions)) {
			this.suggestedTerms = this.suggestions?.suggestedTerms?.slice(0, this.SUGGESTIONCOUNT);
			this.suggestedCatalogs = this.suggestions?.suggestedCatalogs?.slice(0, this.GLOBAL_CATALOGCOUNT)
		}
		this.currentSearchTerm = value;

		this.suggestedCatalogs?.forEach(catalog => {
			if (!this.loadedListItems?.includes(catalog.key)) {
				this.loadedListItems?.push(catalog.key)
			}
		})
		const lastTerm = this.searchbarService.lastSearchResultTerm
		if (!isNullish(lastTerm) && !this.suggestedTerms?.includes(lastTerm)) {
			this.suggestedTerms?.unshift(lastTerm);
		}

	}


	public async searchItem(term: string, isSuggestionClicked: boolean) {

		// Tracking:
		// Hier wird geprüft, ob die Suche aufgrund einer direkten Eingabe des Suchbegriffs ausgeführt wird oder durch Auswahl aus der Vorschlagsliste
		this.trackingFacadeService.handleTrackHistory(new NavigateSearchbarParams(!isNullish(this.indexedSearchterm) && (term === this.indexedSearchterm) || !isSuggestionClicked))

		this.setCatalogViewOrigin();
		await this.navigateToSearch(term);
		this.searchService.refresh();
		this.currentSearchWord = term;
		this.closeSuggestions();
	}

	public async navigateToCatalog(): Promise<void> {

		// Tracking:
		this.trackingFacadeService.handleTrackHistory(new NavigateFromSearchbarIntoCatalog())
		this.trackingFacadeService.handleTrackEvent(new TrackSearchGlobalParams(
			this.currentSearchWord,
			"",
			0,
			0,
			0,
			0,
		))

		this.trackBeforeNavigate();
		if (!isNullish(this.indexedCatalogKey)) {
			await this.router.navigate(['/catalog', this.indexedCatalogKey]);
		}
	}
}
